import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useDonorAuth } from '../context/DonorAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Heart, Mail, Lock, Loader2, ArrowLeft } from 'lucide-react';

const DonorLoginPage = () => {
  const { language } = useLanguage();
  const { login } = useDonorAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const t = {
    fr: {
      title: 'Espace donateur',
      subtitle: 'Connectez-vous pour accéder à votre historique et vos reçus',
      email: 'Courriel',
      password: 'Mot de passe',
      login: 'Se connecter',
      noAccount: 'Pas encore de compte?',
      register: 'Créer un compte',
      success: 'Connexion réussie!',
      backToDonate: 'Retour aux dons',
      forgotPassword: 'Mot de passe oublié?',
    },
    en: {
      title: 'Donor Portal',
      subtitle: 'Log in to access your history and receipts',
      email: 'Email',
      password: 'Password',
      login: 'Log in',
      noAccount: "Don't have an account?",
      register: 'Create account',
      success: 'Login successful!',
      backToDonate: 'Back to donations',
      forgotPassword: 'Forgot password?',
    }
  };

  const text = t[language];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast.success(text.success);
      navigate('/donor/dashboard');
    } else {
      toast.error(result.error);
    }

    setIsSubmitting(false);
  };

  return (
    <div data-testid="donor-login-page" className="min-h-screen bg-gradient-to-b from-[#4A708B]/10 to-white pt-24 pb-16">
      <div className="max-w-md mx-auto px-4 sm:px-6">
        {/* Back Link */}
        <Link 
          to="/donate" 
          className="inline-flex items-center text-[#4A708B] hover:text-[#3B5A6F] mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {text.backToDonate}
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#4A708B]/20 flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-[#4A708B]" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-[#1A252C] mb-2">
            {text.title}
          </h1>
          <p className="text-[#546A76]">{text.subtitle}</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{text.email}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546A76]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="login-email"
                  className="pl-10 rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">{text.password}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546A76]" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="login-password"
                  className="pl-10 rounded-xl border-[#E2E8F0] focus:border-[#4A708B]"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="login-submit"
              className="w-full primary-btn rounded-full py-6 text-lg font-medium"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                text.login
              )}
            </Button>
          </form>

          {/* Register Link */}
          <p className="text-center text-[#546A76] mt-6">
            {text.noAccount}{' '}
            <Link to="/donor/register" className="text-[#D4AF37] font-medium hover:underline">
              {text.register}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonorLoginPage;
